import React from 'react';
import type { IGroupItemProps, IItemProps, ISimpleItemProps } from 'devextreme-react/form';
import type { NuvoEditorType } from './editors';
import editors from './editors';
import type { FormItem, Field, Group } from './FormProps';
import type { RenderProps } from './editors/EditorProps';
import {
  createScrollCloseDropdownOptions,
  isDropdownEditorType,
} from './utils/dropdownScrollClose';
import { formatDateBox } from './utils/dateBoxFormatting';

const getClassByEditorType = (editorType?: string): string => {
  if (!editorType?.length) {
    return '';
  }

  let cssClass = '';
  switch (editorType) {
    case 'RelatedItems':
      cssClass = 'reference-field';
      break;
    case 'RelatedItemsCards':
      cssClass = 'reference-field cards-dropdown';
      break;
    case 'SelectBox':
      cssClass = 'dropdown-field';
      break;
    case 'NumberBox':
      cssClass = 'number-field';
      break;
    case 'Currency':
      cssClass = 'currency-field';
      break;
    case 'DateBox':
      cssClass = 'date-box-field';
      break;
    case 'TextArea':
      cssClass = 'text-area-field';
      break;
  }

  return cssClass;
};

const enhanceEditorOptions = <T extends Record<string, any>>(
  editorType?: string,
  editorOptions?: T,
): T | undefined => {
  if (editorType === 'DateBox') {
    const dateOptions = formatDateBox(editorOptions) as any;
    editorOptions = {
      ...editorOptions,
      ...dateOptions,
    };
  }

  if (isDropdownEditorType(editorType)) {
    const dropdownOptions = editorOptions as any;
    let enhancedDropDownOptions = dropdownOptions?.dropDownOptions || {};

    // prevent the dropdown from centering on the selected item - also add more controls in future
    if (editorType === 'Lookup') {
      editorOptions = {
        ...editorOptions,
        dropDownCentered: false,
      } as unknown as T;
    }

    editorOptions = {
      ...editorOptions,
      dropDownOptions: createScrollCloseDropdownOptions(enhancedDropDownOptions),
    } as unknown as T;
  }
  return editorOptions;
};

const mapField = <T,>(field: Field<T>): ISimpleItemProps => {
  const { label, render, editorType, editorOptions, cssClass, isRequired, ...rest } = field;

  const result: ISimpleItemProps = {
    ...rest,
    editorOptions: enhanceEditorOptions(editorType, editorOptions),
    isRequired,
    itemType: 'simple',
    editorType: editorType ? `dx${editorType}` : undefined,
    cssClass: cssClass ?? getClassByEditorType(editorType),
    render,
    label: {
      visible: label !== false || (!label && !!render),
      text: typeof label === 'string' ? label : undefined,
    },
  };

  const Editor = editors[editorType as NuvoEditorType];
  if (!render && Editor) {
    result.render = (e: RenderProps) => {
      const formData = e.component.option('formData');
      const value = formData?.[e.dataField];
      return (
        <Editor
          form={e.component}
          dataField={e.dataField}
          value={value}
          label={e.editorOptions.label as string}
          editorOptions={e.editorOptions}
          isRequired={isRequired}
        />
      );
    };
  }

  return result;
};

const mapGroup = <T,>(group: Group<T>): IGroupItemProps => {
  const { items, colCount, ...rest } = group;

  const result: IGroupItemProps = {
    ...rest,
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    items: mapFieldItems(items ?? []),
    itemType: 'group',
  };

  if (typeof colCount === 'number') {
    result.colCount = colCount;
  } else {
    result.colCountByScreen = colCount;
  }

  return result;
};

const isGroup = <T,>(item: FormItem<T>): item is Group<T> => {
  return 'items' in item;
};

const mapFieldItem = <T,>(item: FormItem<T>): IItemProps =>
  isGroup(item) ? mapGroup(item) : mapField(item);

const mapFieldItems = <T,>(items: FormItem<T>[]): IItemProps[] => items.map(mapFieldItem);

export const flattenMappedFieldItems = <T,>(items: IItemProps[]): IItemProps[] =>
  items.map((item) => (isGroup(item) ? flattenMappedFieldItems(item.items) : [item])).flat(1);

export default mapFieldItems;
